import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { PolicyDataService } from '../../http-services/services/policy-data.service';
import { UtilsService } from '../../http-services/services/utils.service';
import { Coverage } from '../../interfaces/coverage.model';
import { Customer } from '../../interfaces/customer.model';
import { PolicyData } from '../../interfaces/policy-data.model';
import { ValidationError } from '../../interfaces/validation-error.model';
import { CoverageCode } from '../enums/coverage-code.enum';
import { CoverageType } from '../enums/coverage-type.enum';
import { CurrencyPipe } from '../pipes/currency.pipe';
import { DataService } from '../services/data.service';
import { GlobalErrorHandler } from '../services/error-handler.service';
import { ReferenceDataService } from '../services/reference-data.service';
import { ReferenceDataSet } from '../services/reference-data.types';
import { calculateAge } from '../utils/get-age';

@Component({
  selector: 'app-base.widget.component',
  imports: [],
  template: '', // No template defined
  providers: [CurrencyPipe],
  styles: [],
})
export abstract class BaseWidgetComponent implements OnInit, OnDestroy {
  protected readonly _referenceDataService: ReferenceDataService = inject(ReferenceDataService);
  protected readonly _errorHandlingService: GlobalErrorHandler = inject(GlobalErrorHandler);
  protected readonly _baseDataService: DataService = inject(DataService);
  protected readonly _httpPolicyDataService: PolicyDataService = inject(PolicyDataService);
  protected readonly _router: Router = inject(Router);
  protected readonly _route: ActivatedRoute = inject(ActivatedRoute);
  protected readonly _utilsService: UtilsService = inject(UtilsService);

  protected readonly _baseSubscriptions: Subscription = new Subscription(); // Hold all subscriptions
  protected _dataSubscription: Subscription = new Subscription();

  sharedPolicyData?: PolicyData;
  previousSumInsured: number | null = null;
  isLoading: boolean = false;

  CoverageType = CoverageType; // Expose enum to template
  CoverageCode = CoverageCode; // Expose enum to template

  ngOnInit(): void {
    // Subscribe to data updates
    this._dataSubscription = this._baseDataService.data$.subscribe(data => {
      if (data) {
        this.sharedPolicyData = structuredClone(data);
      }
    });

    if (this._dataSubscription) {
      this.addSubscription(this._dataSubscription);
    }
  }

  ngOnDestroy(): void {
    this._baseSubscriptions.unsubscribe(); // Unsubscribe from all subscriptions to avoid memory leaks
  }

  /**
   * Determines whether the base quote has changed by comparing customers and coverages.
   * Checks for removed customers, removed coverages, or any changes to coverage values.
   *
   * @returns {boolean} True if the base quote has changed; false otherwise.
   */
  get _baseQuoteHasChanged(): boolean {
    if (Object.keys(this._baseDataService.get('updatedQuote')).length > 0) {
      const customerRemoved =
        this._baseDataService.get('updatedQuote').customers.length !==
        this._baseDataService.get('originalLoadedQuote').customers.length;

      const coveragesRemoved =
        this._baseDataService.get('updatedQuote').coverages.length !==
        this._baseDataService.get('originalLoadedQuote').coverages.length;

      const coverageChanges = this._baseDataService
        .get('updatedQuote')
        .coverages.filter(
          (coverage: Coverage) =>
            coverage?.code !== 'uPremium' && coverage?.sumInsured?.amount !== coverage?.sumInsuredOriginal,
        );
      const coverageHasChanged = coverageChanges.length > 0;
      return coverageHasChanged || coveragesRemoved || customerRemoved;
    } else {
      return false;
    }
  }

  protected selectTab(tab: string) {
    this._baseDataService.set('selectedTab', tab);
  }

  protected addSubscription(subscription: Subscription): void {
    this._baseSubscriptions.add(subscription); // Add a new subscription to the existing ones
  }

  /**
   * Triggers the premium calculation for the policy and updates the sharedPolicyData.
   * Also sets error and loading state as appropriate, recalculates customer ages,
   * syncs coverage premiums and summary amounts, and manages policy feedback/errors.
   *
   * @returns {void}
   */
  protected async _baseTriggerPremiumCalculation(): Promise<void> {
    this.isLoading = true; // Show spinner

    return this._httpPolicyDataService
      .premiumCalculation(this.sharedPolicyData!)
      .then((response: PolicyData) => {
        const prevData = structuredClone(this.sharedPolicyData!);
        this.sharedPolicyData = response; // Update data

        if (prevData.feedbacks) {
          this.sharedPolicyData.feedbacks = prevData.feedbacks;
        }
        if (prevData.rulesFeedbacks) {
          this.sharedPolicyData.rulesFeedbacks = prevData.rulesFeedbacks;
        }

        // Re-calculate ages for each customer
        if (this.sharedPolicyData?.customers && Array.isArray(this.sharedPolicyData.customers)) {
          this.sharedPolicyData.customers.forEach((c: Customer) => {
            const age = calculateAge(c.dateOfBirth);
            c.age = age; // assign the age property
          });
        }
        this.updateCoverageOriginals(prevData, this.sharedPolicyData);

        let hasErrors = false;
        let isRemoveCoverError = false;
        if (this.sharedPolicyData?.evaluationResponse) {
          const res = this.sharedPolicyData.evaluationResponse;
          if (!res) {
            console.warn('Evaluation response is not yet available.');
          } else {
            // Iterate over coverages and assign premiumCurrent
            this.sharedPolicyData.coverages?.forEach((coverage: Coverage) => {
              coverage.premiumCurrent = coverage?.premium?.amount ?? 0;
            });
            hasErrors = res?.errors!.length > 0;
            isRemoveCoverError = res.errors?.some(error => error.message.startsWith('Remove Cover - '))!;

            if (hasErrors && !isRemoveCoverError) {
              this.openAccordions(res.errors);
            }
          }
        }

        if (!hasErrors) {
          this._baseDataService.set('updatedQuote', this.sharedPolicyData);
        }
        this._baseDataService.updateData(this.sharedPolicyData);
      })
      .catch(error => {
        throw error;
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  /**
   * Expands accordions in the UI based on the provided validation errors.
   * Processes the errors to determine which customer and coverage sections need to be expanded
   * for error visibility and user correction. Updates the expandedCoverages state accordingly.
   *
   * @param {ValidationError[]} [errors] - An optional array of validation errors related to the policy.
   * @returns {void}
   */
  openAccordions(errors?: ValidationError[]) {
    if (errors) {
      const customerIndices = errors.flatMap(error =>
        error.path
          .filter(p => p.startsWith('customers['))
          .map(p => {
            // Remove 'customers[' and ']', then convert to number
            const numStr = p.replace('customers[', '').replace(']', '');
            const num = Number(numStr);
            return isNaN(num) ? null : num;
          })
          .filter(n => n !== null),
      );

      const accordionState: { customer: number; accordions: number[] }[] = customerIndices
        .map(index => {
          const customerObj = this.sharedPolicyData?.customers?.[index];
          const customerNumber = customerObj?.number;
          return {
            customer: customerNumber ? parseInt(customerNumber) : 0,
            accordions: [],
          };
        })
        .filter(item => item.customer !== null);
      errors.forEach((error: ValidationError) => {
        const loweredPaths = error.path.map(p => p.toLowerCase());

        const customer = loweredPaths.find(p => p.startsWith('customers['));
        const coverage = loweredPaths.find(p => p.startsWith('coverages['));

        if (customer && coverage) {
          const customerIndex = parseInt(customer.replace('customers[', '').replace(']', ''));
          const coverageIndex = parseInt(coverage.replace('coverages[', '').replace(']', ''));
          if (!isNaN(customerIndex) && !isNaN(coverageIndex) && coverageIndex > 0) {
            const customerNumberFromIndex = parseInt(this.sharedPolicyData?.customers![customerIndex].number!);
            const customerAccordion = accordionState.find(state => state.customer === customerNumberFromIndex);
            if (customerAccordion && !customerAccordion.accordions.includes(coverageIndex)) {
              customerAccordion.accordions.push(customerAccordion.accordions.length);
            }
          }
        }
      });
      this._baseDataService.set('expandedCoverages', [...accordionState]);
    }
  }

  /**
   * Updates the coverages in the current policy data with the original sum insured and premium
   * values from the previous policy data. This helps ensure any adjusted or retained values
   * are tracked for UI and calculation purposes.
   *
   * @param {PolicyData} lastData - The previous policy data object used as the source for original values.
   * @param {PolicyData} currentData - The current policy data object to update.
   * @returns {void}
   */
  // eslint-disable-next-line complexity
  updateCoverageOriginals(lastData: PolicyData, currentData: PolicyData): void {
    if (!lastData?.coverages || !currentData?.coverages) {
      return; // guard against missing data
    }

    for (let i = 0; i < lastData.coverages.length; i++) {
      const lastCoverage = lastData.coverages[i];
      const currCoverage = currentData.coverages[i];

      if (lastCoverage && currCoverage) {
        // Initialize sumInsuredOriginal and premiumOriginal if they don't exist
        currCoverage.sumInsuredOriginal ??= currCoverage.sumInsuredOriginal ?? 0;
        currCoverage.premiumOriginal ??= currCoverage.premiumOriginal ?? 0;
        currCoverage.premiumCurrent ??= currCoverage.premiumCurrent ?? 0;

        // Update to last known values (likely adjusted/retained)
        currCoverage.sumInsuredOriginal = lastCoverage.sumInsuredOriginal ?? currCoverage.sumInsured?.amount ?? 0;
        currCoverage.premiumOriginal = lastCoverage.premiumOriginal ?? currCoverage.premium?.amount ?? 0;
        currCoverage.premiumCurrent = lastCoverage.premiumCurrent ?? currCoverage.premium?.amount ?? 0;
      }
    }
  }

  protected getRefDS(refDataSetName: string): ReferenceDataSet | undefined {
    return this._referenceDataService.getReferenceDataSet(refDataSetName);
  }

  showError(message: string) {
    this._errorHandlingService.setMessage(message);
    this._errorHandlingService.showError();
  }

  protected _baseGetValByCode(refDataSetName: string, refCode?: string | null): string | undefined {
    if (refCode != null) {
      return this._referenceDataService.getReferenceDataSetValue(refDataSetName, refCode);
    }
    return;
  }
}
